import {
  areSocketTypesCompatible,
  extractWorkflowExpressionReferences,
  isArraySocketType,
  isFlowSocket,
} from '@erp/workflows';
import type { BuilderNode } from './builderTree';
import {
  argumentType,
  getBuilderFunction,
  literalArgumentError,
} from './builderFunctions';

/**
 * What the builder knows about the graph around the socket it is editing:
 * which upstream outputs can feed it, and how a value's type sits against the
 * slot it fills. Pure — the editor adapts its React Flow nodes into
 * {@link BuilderGraphStep}s and hands them in.
 */

export interface BuilderGraphSocket {
  id: string;
  name: string;
  type: string;
}

export interface BuilderGraphStep {
  id: string;
  label: string;
  inputs: BuilderGraphSocket[];
  outputs: BuilderGraphSocket[];
  /** Manual overrides by input socket id (see `readManualExpressionOverrides`). */
  overrides: Readonly<Record<string, string | null>>;
}

export interface BuilderGraphEdge {
  source: string;
  target: string;
}

/** One upstream output a slot can read. */
export interface BuilderSource {
  stepId: string;
  stepLabel: string;
  socketId: string;
  socketName: string;
  type: string;
}

export function sourceKey(stepId: string, socketId: string): string {
  return `${stepId}:${socketId}`;
}

/** Override references as step → step dependencies, unparseable ones skipped. */
function overrideDependencies(steps: readonly BuilderGraphStep[]) {
  const dependencies: BuilderGraphEdge[] = [];
  for (const step of steps) {
    for (const expression of Object.values(step.overrides)) {
      if (typeof expression !== 'string') continue;
      try {
        for (const ref of extractWorkflowExpressionReferences(expression)) {
          dependencies.push({ source: ref.stepId, target: step.id });
        }
      } catch {
        // An unparseable override has no references to follow.
      }
    }
  }
  return dependencies;
}

/** The target step and every step that (transitively) reads from it. */
function downstreamOf(
  targetStepId: string,
  dependencies: readonly BuilderGraphEdge[],
): Set<string> {
  const reached = new Set([targetStepId]);
  const queue = [targetStepId];
  while (queue.length > 0) {
    const current = queue.shift();
    for (const dependency of dependencies) {
      if (dependency.source !== current || reached.has(dependency.target)) {
        continue;
      }
      reached.add(dependency.target);
      queue.push(dependency.target);
    }
  }
  return reached;
}

/**
 * Every output socket the target step may read: all data outputs on steps
 * that are not the target and do not depend on it — reading a downstream
 * step's output would be a cycle. Grouped by step, in canvas order.
 */
export function listInScopeSources(
  steps: readonly BuilderGraphStep[],
  edges: readonly BuilderGraphEdge[],
  targetStepId: string,
): BuilderSource[] {
  const excluded = downstreamOf(targetStepId, [
    ...edges,
    ...overrideDependencies(steps),
  ]);
  return steps.flatMap((step) =>
    excluded.has(step.id)
      ? []
      : step.outputs.flatMap((socket) =>
          isFlowSocket(socket.id)
            ? []
            : [
                {
                  stepId: step.id,
                  stepLabel: step.label,
                  socketId: socket.id,
                  socketName: socket.name,
                  type: socket.type,
                },
              ],
        ),
  );
}

/** Every data output on the canvas, keyed by {@link sourceKey}. */
export function indexAllSources(
  steps: readonly BuilderGraphStep[],
): Map<string, BuilderSource> {
  const index = new Map<string, BuilderSource>();
  for (const step of steps) {
    for (const socket of step.outputs) {
      index.set(sourceKey(step.id, socket.id), {
        stepId: step.id,
        stepLabel: step.label,
        socketId: socket.id,
        socketName: socket.name,
        type: socket.type,
      });
    }
  }
  return index;
}

const SCALAR_TYPES = new Set(['string', 'number', 'boolean']);

/**
 * Whether a socket type carries objects a field can be read from — a `json`
 * value, or a list of `json` rows (`get` maps over the rows).
 */
export function isObjectLikeType(type: string): boolean {
  return type === 'json' || type === 'array<json>';
}

/** The socket type a builder node yields, when it can be known statically. */
export function nodeValueType(
  node: BuilderNode,
  sources: ReadonlyMap<string, BuilderSource>,
): string | undefined {
  switch (node.kind) {
    case 'empty':
      return undefined;
    case 'literal':
      return typeof node.value === 'number' ? 'number' : 'string';
    case 'source':
      // A field's type is not known: sockets carry no object schema.
      return node.field
        ? undefined
        : sources.get(sourceKey(node.stepId, node.socketId))?.type;
    case 'call':
      return callValueType(node, sources);
  }
}

/**
 * A transform handed a batch (an array where it takes a scalar) maps over
 * it, so its result is a batch too: `substr` of an `array<string>` column
 * yields `array<string>`, not `string`.
 */
function callValueType(
  node: Extract<BuilderNode, { kind: 'call' }>,
  sources: ReadonlyMap<string, BuilderSource>,
): string | undefined {
  const outputType = getBuilderFunction(node.fn)?.outputType;
  if (!outputType) return undefined;
  const batched = node.args.some((arg, index) => {
    const argType = nodeValueType(arg, sources);
    return (
      argType !== undefined &&
      isArraySocketType(argType) &&
      SCALAR_TYPES.has(argumentType(node.fn, index))
    );
  });
  return batched ? `array<${outputType}>` : outputType;
}

export type SlotIssue =
  'unknown-source' | 'needs-field' | 'type-mismatch' | 'invalid-value';

/** The call a slot is an argument of, for checks the slot type can't make. */
export interface SlotArgument {
  fn: string;
  index: number;
}

/**
 * What is wrong with `node` in a slot expecting `slotType`, if anything.
 *
 * - `unknown-source`: the step or its output socket is gone from the canvas.
 * - `needs-field`: an object (or list of objects) where a scalar is wanted —
 *   the fix is choosing a property, not a different source.
 * - `type-mismatch`: a statically known type the slot cannot accept.
 * - `invalid-value`: a literal the function's own schema rejects.
 */
export function slotIssue(
  slotType: string,
  node: BuilderNode,
  sources: ReadonlyMap<string, BuilderSource>,
  argument?: SlotArgument,
): SlotIssue | null {
  const early =
    node.kind === 'literal'
      ? literalIssue(node.value, argument)
      : node.kind === 'source'
        ? sourceIssue(slotType, node, sources)
        : null;
  if (early) return early;
  const valueType = nodeValueType(node, sources);
  if (!valueType || slotType === 'any' || valueType === 'any') return null;
  return areSocketTypesCompatible(valueType, slotType) ? null : 'type-mismatch';
}

function literalIssue(
  value: string | number,
  argument: SlotArgument | undefined,
): SlotIssue | null {
  if (!argument) return null;
  return literalArgumentError(argument.fn, argument.index, value)
    ? 'invalid-value'
    : null;
}

function sourceIssue(
  slotType: string,
  node: Extract<BuilderNode, { kind: 'source' }>,
  sources: ReadonlyMap<string, BuilderSource>,
): SlotIssue | null {
  const source = sources.get(sourceKey(node.stepId, node.socketId));
  if (!source) return 'unknown-source';
  const wantsScalar = !node.field && SCALAR_TYPES.has(slotType);
  return wantsScalar && isObjectLikeType(source.type) ? 'needs-field' : null;
}

/**
 * Whether picking `source` for a slot expecting `slotType` could work, so the
 * pickers can dim the rest. Object sources stay pickable for scalar slots: the
 * row then asks for a property.
 */
export function canSourceFillSlot(
  source: BuilderSource,
  slotType: string,
): boolean {
  if (slotType === 'any' || source.type === 'any') return true;
  if (SCALAR_TYPES.has(slotType) && isObjectLikeType(source.type)) return true;
  return areSocketTypesCompatible(source.type, slotType);
}
